import { useRef } from "react";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, UploadSimple, Trash, Crop, ArrowsClockwise, ArrowClockwise } from "@phosphor-icons/react";

const pill = "flex items-center gap-1 rounded-full border border-[#4A4540]/20 px-3 py-1 text-xs font-medium text-[#4A4540] transition-colors hover:border-[#E07A5A] hover:text-[#A84A32] disabled:opacity-50";

export function PhotoSlot({ index, photo, targets, busy, disabled, onFile, onTarget, onCrop, onRotate, onRemove }) {
  const inputRef = useRef(null);
  return (
    <div data-testid={`photo-slot-${index}`} className="ph-no-capture flex flex-col rounded-2xl border border-[#4A4540]/15 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#8A7F76]">Photo {index + 1}</span>
        {photo && (
          <span className="flex items-center gap-1 rounded-full bg-[#EAF2EC] px-2 py-0.5 text-[11px] font-semibold text-[#2E6B4F]">
            <ShieldCheck size={12} weight="fill" /> On your device
          </span>
        )}
      </div>

      {photo ? (
        <>
          <div className="relative overflow-hidden rounded-xl bg-[#F4EFE6]">
            <img data-testid={`photo-thumb-${index}`} src={photo.dataUrl} alt={photo.name} className="h-44 w-full object-contain" />
            {busy && <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs text-[#8A7F76]">Preparing…</div>}
          </div>
          <p className="mt-2 truncate text-xs text-[#8A7F76]" title={photo.name}>{photo.name}</p>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-[#4A4540]">Place this photo in…</label>
            <Select value={photo.target} onValueChange={(v) => onTarget(index, v)}>
              <SelectTrigger data-testid={`photo-match-${index}`} className="border-[#4A4540]/20 bg-white">
                <SelectValue placeholder="Choose cover, season or memory" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((g) => (
                  <SelectGroup key={g.group}>
                    <SelectLabel>{g.group}</SelectLabel>
                    {g.items.map((it) => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" data-testid={`photo-crop-${index}`} disabled={busy} onClick={() => onCrop(index)} className={pill}><Crop size={14} /> Crop</button>
            <button type="button" data-testid={`photo-rotate-${index}`} disabled={busy} onClick={() => onRotate(index)} className={pill}><ArrowClockwise size={14} /> Rotate</button>
            <button type="button" data-testid={`photo-replace-${index}`} disabled={busy} onClick={() => inputRef.current?.click()} className={pill}><ArrowsClockwise size={14} /> Replace</button>
            <button type="button" data-testid={`photo-remove-${index}`} onClick={() => onRemove(index)} className="flex items-center gap-1 rounded-full border border-[#C0533B]/30 px-3 py-1 text-xs font-medium text-[#C0533B] transition-colors hover:bg-[#F6E2D8]"><Trash size={14} /> Remove</button>
          </div>
        </>
      ) : (
        <button
          type="button"
          data-testid={`photo-add-${index}`}
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
          className="flex h-56 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#4A4540]/25 bg-[#F4EFE6] text-[#8A7F76] transition-colors hover:border-[#E07A5A] hover:text-[#A84A32] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <UploadSimple size={26} />
          <span className="text-sm font-medium">{busy ? "Preparing…" : "Add a photo from this device"}</span>
          <span className="text-[11px]">Stays on your phone / computer</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid={`photo-file-input-${index}`}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(index, f);
          e.target.value = "";
        }}
      />
    </div>
  );
}
